import { Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
	ContactPointCategory,
	ContactPointKind,
	isEmailContactPointCategory,
	isFaxContactPointCategory,
	isPhoneContactPointCategory,
	isSmsContactPointCategory,
	isWebContactPointCategory,
	resolveContactPointKind,
} from '../../../../api/app-types/contact-point.app';
import { InstitutionExtended } from '../../../../api/app-types/institution.app';
import { LvrBorderDirective } from '../../../../shared/border/border';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';

@Component({
	selector: 'lvr-institution-details-view',
	templateUrl: './institution-details-view.html',
	styleUrl: './institution-details-view.scss',
	imports: [MatIconModule, TranslocoPipe, LvrBorderDirective, LvrPaddingDirective],
})
export class InstitutionDetailsView {
	public readonly institution = input.required<InstitutionExtended>();

	private readonly route = inject(ActivatedRoute);
	private readonly transloco = inject(TranslocoService);
	private readonly data = toSignal(this.route.data);

	public readonly contactPointTypes = computed(
		() => (this.data()?.['contactPointTypes'] as PaginatedResponse<ContactPointCategory>)?.entities ?? [],
	);

	private readonly contactPointTypesById = computed<ReadonlyMap<number, ContactPointCategory>>(() => {
		return new Map(this.contactPointTypes().map((contactPointType) => [contactPointType.id, contactPointType]));
	});

	public readonly totalWorkplaces = computed(
		() => (this.data()?.['workplaces'] as PaginatedResponse<unknown>)?.totalEntities ?? 0,
	);

	public readonly preferredCommunicationWay = computed(() =>
		this.institution().contactPoints.find((contactPoint) => contactPoint.preferredWayOfContact),
	);

	public readonly communicationWays = computed(() => {
		const preferredCommunicationWay = this.preferredCommunicationWay();

		return this.institution().contactPoints.filter((contactPoint) => contactPoint !== preferredCommunicationWay);
	});

	private getContactPointTypeCategory(categoryContactPointId?: number): ContactPointCategory | undefined {
		if (categoryContactPointId == null) {
			return undefined;
		}

		return this.contactPointTypesById().get(categoryContactPointId);
	}

	public getContactPointKind(categoryContactPointId?: number): ContactPointKind {
		return resolveContactPointKind(categoryContactPointId);
	}
	public isPhoneType(categoryContactPointId?: number): boolean {
		return isPhoneContactPointCategory(categoryContactPointId);
	}

	public isEmailType(categoryContactPointId?: number): boolean {
		return isEmailContactPointCategory(categoryContactPointId);
	}

	public isSmsType(categoryContactPointId?: number): boolean {
		return isSmsContactPointCategory(categoryContactPointId);
	}

	public isFaxType(categoryContactPointId?: number): boolean {
		return isFaxContactPointCategory(categoryContactPointId);
	}

	public isWebType(categoryContactPointId?: number): boolean {
		return isWebContactPointCategory(categoryContactPointId);
	}

	private getCommunicationTypeLabelKey(categoryContactPointId?: number): string {
		const kind = this.getContactPointKind(categoryContactPointId);

		return `institution.details.communication_type.${kind}`;
	}

	public getCommunicationTypeLabel(categoryContactPointId?: number): string {
		const category = this.getContactPointTypeCategory(categoryContactPointId);

		if (category?.remark?.trim()) {
			return category.remark.trim();
		}

		return this.transloco.translate(this.getCommunicationTypeLabelKey(categoryContactPointId));
	}

	public shouldShowRemark(remark?: string, categoryContactPointId?: number): boolean {
		const normalizedRemark = remark?.trim().toLowerCase();

		if (!normalizedRemark) {
			return false;
		}

		const categoryRemark = this.getContactPointTypeCategory(categoryContactPointId)?.remark?.trim().toLowerCase();

		if (!categoryRemark) {
			return true;
		}

		return normalizedRemark !== categoryRemark;
	}
}
